// /cms-frontend/src/services/exams.ts

import api from "./api";

export interface Exam {
  _id: string;
  branchId: string;
  courseId: {
    _id: string;
    name: string;
    code: string;
  };
  teacherId: string;
  title: string;
  description?: string;
  type: "physical" | "online";
  quizId?: string;
  schedule: {
    date: string;
    startTime: string;
    endTime: string;
    duration: number;
  };
  venue?: string;
  maxMarks: number;
  weightage: number;
  status: "scheduled" | "ongoing" | "completed" | "cancelled" | "archived";
  instructions?: string;
  gradeSubmissionDeadline?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Grade {
  _id: string;
  examId: string;
  studentId: string;
  courseId: string;
  marks: number;
  maxMarks: number;
  percentage: number;
  grade: string;
  remarks?: string;
  quizAttemptId?: string;
  status: string;
  submittedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateExamData {
  courseId: string;
  title: string;
  description?: string;
  type: "physical" | "online";
  quizId?: string;
  schedule: {
    date: string;
    startTime: string;
    endTime: string;
    duration: number;
  };
  venue?: string;
  maxMarks: number;
  weightage: number;
  instructions?: string;
  gradeSubmissionDeadline?: string;
}

export interface GradeSubmission {
  studentId: string;
  marks: number;
  remarks?: string;
}

// Create a new exam
export const createExam = async (examData: CreateExamData): Promise<Exam> => {
  const response = await api.post("/exams", examData);
  return response.data;
};

// Get exams for a specific course
export const getExamsForCourse = async (courseId: string): Promise<Exam[]> => {
  const response = await api.get(`/exams/course/${courseId}`);
  return response.data;
};

// Get exams for the current teacher
export const getTeacherExams = async (): Promise<Exam[]> => {
  const response = await api.get("/exams/teacher");
  return response.data;
};

// Update an exam
export const updateExam = async (
  examId: string,
  updateData: Partial<CreateExamData>
): Promise<Exam> => {
  const response = await api.put(`/exams/${examId}`, updateData);
  return response.data;
};

// Archive an exam
export const archiveExam = async (examId: string): Promise<Exam> => {
  const response = await api.put(`/exams/${examId}/archive`);
  return response.data;
};

// Unarchive an exam
export const unarchiveExam = async (examId: string): Promise<Exam> => {
  const response = await api.put(`/exams/${examId}/unarchive`);
  return response.data;
};

// Delete an exam
export const deleteExam = async (examId: string): Promise<void> => {
  await api.delete(`/exams/${examId}`);
};

// Submit grades for a physical exam
export const submitExamGrades = async (
  examId: string,
  grades: GradeSubmission[]
): Promise<void> => {
  await api.post(`/exams/${examId}/grades`, { grades });
};

// Get grades for an exam
export const getExamGrades = async (examId: string): Promise<Grade[]> => {
  const response = await api.get(`/exams/${examId}/grades`);
  return response.data;
};

// Get student grades for a course
export const getStudentGradesForCourse = async (
  courseId: string
): Promise<{
  course: {
    id: string;
    name: string;
    code: string;
  } | null;
  grades: Grade[];
  overallGrade: {
    overallPercentage: number;
    overallGrade: string;
    examCount: number;
  } | null;
}> => {
  const response = await api.get(`/exams/course/${courseId}/grades`);
  return response.data; // The axios interceptor returns response.data, so response is already the data object
};

// Publish grades for an exam
export const publishExamGrades = async (examId: string): Promise<void> => {
  await api.put(`/exams/${examId}/publish-grades`);
};

// Get upcoming exams for student
export const getUpcomingExams = async (): Promise<Exam[]> => {
  const response = await api.get("/exams/student/upcoming");
  return response.data;
};
